import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Empty from 'antd/lib/empty';
import Skeleton from 'antd/lib/skeleton';
import { getArticleByIdentifier } from '../api/articles';
import FooterBar from '../components/helpdesk/FooterBar';
import HeaderBar from '../components/helpdesk/HeaderBar';
import SupportSidebar from '../components/helpdesk/SupportSidebar';
import { mapApiArticleToCard } from '../api/articles.mappers';
import '../styles/helpdesk.scss';

function PublicArticlePage() {
  const { identifier } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    async function loadArticle() {
      setLoading(true);
      setNotFound(false);

      try {
        const response = await getArticleByIdentifier(identifier);
        const status = String(response?.status || '').toUpperCase();
        if (status !== 'PUBLISHED') {
          setNotFound(true);
          return;
        }
        setArticle(response);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }

    loadArticle();
  }, [identifier]);

  const articleMeta = mapApiArticleToCard(article || {});
  const normalizedContent = useMemo(() => {
    const rawContent = article?.content || '';
    if (!rawContent) return '';

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(rawContent, 'text/html');

      doc.body.querySelectorAll('*').forEach((node) => {
        node.style.removeProperty('background');
        node.style.removeProperty('background-color');
        node.style.removeProperty('color');
      });

      return doc.body.innerHTML;
    } catch {
      return rawContent;
    }
  }, [article?.content]);

  return (
    <div className="helpdesk-page">
      <HeaderBar />
      <main className="helpdesk-main">
        <div className="helpdesk-main__inner">
          <section className="content-grid content-grid--article" aria-label="Article and resources">
            <article className="public-article-card">
              <section className="public-article">
                {loading ? (
                  <div className="public-article__state">
                    <Skeleton active paragraph={{ rows: 8 }} />
                  </div>
                ) : notFound ? (
                  <div className="public-article__state public-article__state--empty">
                    <Empty description="Published article not found" />
                  </div>
                ) : (
                  <>
                    <Link to="/articles" className="create-article__back">
                      {'<-'} Back to Articles
                    </Link>
                    <header className="public-article__header">
                      <p className="public-article__kicker">{articleMeta.category}</p>
                      <h1>{articleMeta.title}</h1>
                      <p className="public-article__excerpt">{articleMeta.description}</p>

                      <div className="public-article__meta">
                        <span className="public-article__author">{articleMeta.author}</span>
                        <span>{articleMeta.date}</span>
                      </div>
                    </header>

                    {article?.featuredImageUrl ? (
                      <div className="public-article__hero">
                        <img src={article.featuredImageUrl} alt={articleMeta.title} loading="lazy" />
                      </div>
                    ) : null}

                    <article
                      className="public-article__content"
                      dangerouslySetInnerHTML={{ __html: normalizedContent }}
                    />
                  </>
                )}
              </section>
            </article>

            <SupportSidebar />
          </section>
        </div>
      </main>
      <FooterBar />
    </div>
  );
}

export default PublicArticlePage;
